<script setup lang="ts">
import { navigateTo } from '@/ts/management/view-navigation-management';
import model from '@/ts/model';
import inboxImg from "./assets/bell.svg?raw";
import { nextTick, ref, watch } from 'vue';
import type { OptimoveInboxMessage } from '@/ts/types/optimove-types.ts';
import { stringToNumber } from '@/ts/utils/utils.ts';

const NOTIF_MSG_DISPOSE_TIME: string = '10000ms';

const notifMsg = ref<OptimoveInboxMessage>();

let loginModeTimeout = 0;
let nullifyNotifMsgTimeout = 0;

watch(() => model.inbox.messages.length, async () => {
    const messages = model.inbox.messages;
    const firstRequest = model.inbox.firstReqAfterLogin;

    clearTimeout(loginModeTimeout);
    notifMsg.value = undefined;
    
    await nextTick();

    const firstUnread = messages.find(x => !x.readAt);
    if(firstUnread?.payload?.DELIVERY_MODE == 'NOTIFY') {
        notifMsg.value = firstUnread;
    }
    else if(firstRequest){
        const firstLoginModeNotifyMsg = messages.find(x => x.payload?.LOGIN_MODE == 'NOTIFY')
        if(firstLoginModeNotifyMsg && !model.inbox.unreadMsgs) {
            loginModeTimeout = setTimeout(() => {
                notifMsg.value = firstLoginModeNotifyMsg;
            }, 6000);
        }
    }
}, {immediate: true});

watch(() => notifMsg.value, value => {
    clearTimeout(nullifyNotifMsgTimeout);
    if(!value) return;

    nullifyNotifMsgTimeout = setTimeout(() => {
        notifMsg.value = undefined;
    }, stringToNumber(NOTIF_MSG_DISPOSE_TIME));
}, {immediate: true});

function onInboxClick(){
    if(notifMsg.value){
        model.inbox.autoOpenMsg = notifMsg.value;
    }

    navigateTo('inbox');
}

</script>

<template>
    <div 
        class="btn-inbox" 
        @click.prevent="onInboxClick()" 
        :data-content="model.inbox.unreadMsgs" 
        :class="{'has-unread-msgs': model.inbox.unreadMsgs}" 
        v-mobile
    >
        <div v-html="inboxImg" class="icon" :class="{ringing: notifMsg}"></div>
        <template v-if="notifMsg">
            <div class="ringing-visual-sound"></div>
            <div class="notif-msg" v-mobile>
                <div class="inner">{{ notifMsg.title }}</div>
            </div>
        </template>
    </div>
</template>

<style lang="scss" scoped>
    @use '@/scss/shared';
    @mixin btn-inbox-unread-msgs(){
        &.has-unread-msgs{
            &:after{
                position: absolute;
                display: flex;
                content: attr(data-content);
                width: 20px;
                height: 20px;
                color: #000;
                border-radius: 100%;
                top: 7px;
                right: 7px;
                justify-content: center;
                align-items: center;
                font-size: 13px;
                font-weight: 600;
                pointer-events: none;
                animation: pulse 1s infinite ease-in-out alternate;
                z-index: 1;
                @keyframes pulse {
                    from { transform: scale(.95); }
                    to { transform: scale(1.10); }
                }
                @include shared.site() using($site-key){
                    background: linear-gradient(shared.theme-color('comp5_2', $site-key), shared.theme-color('comp5_5', $site-key));
                }
            }
            &[data-mobile]{
                &:after{
                    top: -6px;
                    right: -2px;
                    font-size: 11px;
                    width: 15px;
                    height: 15px;
                }
            }
        }
    }
    .btn-inbox{
        position: relative;
        cursor: pointer;
        display: flex;
        justify-content: center;
        align-items: center;
        color: #fff;
        transition: color .25s;
        z-index: 1;
        @include btn-inbox-unread-msgs();
        &[data-mobile]{
            padding: 0 8px;
            margin-right: 7px;
        }
        &:not([data-mobile]){
            width: 60px;
            height: 60px;
            &:hover{
                @include shared.site() using($site-key){
                    color: shared.theme-color("comp3_3", $site-key);
                }
            }
        }
        & > .ringing-visual-sound,
        & > .icon.ringing{
            animation-fill-mode: forwards;
            animation-duration: 2s;
            animation-iteration-count: 5;
        }
        & > .ringing-visual-sound{
            position: absolute;
            display: block;
            content: "";
            width: 50px;
            height: 50px;
            z-index: 1;
            border: 5px solid #fff;
            border-radius: 100%;
            opacity: 0;
            pointer-events: none;
            animation-name: icon-ringing-sound;
            animation-timing-function: linear;
            @keyframes icon-ringing-sound {
                0%{
                    opacity: 0;
                    transform: scale(0);
                }
                25%{
                    opacity: 0;
                    transform: scale(.60);
                }
                40%{
                    opacity: .5;
                    transform: scale(.90);
                }
                100%{
                    opacity: 0;
                    transform: scale(1.10);
                }
            }
        }
        & > .icon{
            position: relative;
            display: flex;
            justify-content: center;
            align-items: center;
            &.ringing{
                animation-name: icon-ringing;
                @keyframes icon-ringing {
                    0%{
                        transform: scale(1);
                    }
                    10%{
                        transform: scale(1.4);
                    }
                    20%{
                        transform: scale(1.5);
                    }
                    24%{
                        transform: scale(1.5) rotate(20deg);
                    }
                    28%{
                        transform: scale(1.9) rotate(0deg);
                    }
                    32%{
                        transform: scale(1.85) rotate(15deg);
                    }
                    36%{
                        transform: scale(1.80) rotate(0deg);
                    }
                    40%{
                        transform: scale(1.75) rotate(10deg);
                    }
                    44%{
                        transform: scale(1.70) rotate(0deg);
                    }
                    48%{
                        transform: scale(1.65) rotate(5deg);
                    }
                    50%{
                        transform: scale(1.60) rotate(0deg);
                    }
                }
            }
        }
        & > .notif-msg{
            display: block;
            position: absolute;
            bottom:-30px;
            right: -40px;
            animation-fill-mode: forwards;
            animation-duration: v-bind(NOTIF_MSG_DISPOSE_TIME);
            animation-name: silent-notif-display;
            @mixin trans-origin($left){
                transform-origin: $left top;
                &:after{
                    left: $left;
                }
            }
            @include trans-origin(80%);
            &[data-mobile]{
                @include trans-origin(83%);
            }
            &:after{
                position: absolute;
                content: "";
                top:0;
                width: 10px;
                height: 10px;
                transform: translateX(-50%) translateY(-50%) rotate(45deg);
                z-index: -1;
                @include shared.site() using($site-key){
                    background-color: shared.theme-color('comp2_3', $site-key);
                }
            }
            & > .inner{
                position: relative;
                color: #fff;
                text-shadow: 0 0 2px #000, 0 0 2px #000, 0 0 5px #000;
                font-size: 13px;
                pointer-events: none;
                white-space: nowrap;
                padding: 5px 15px;
                border-radius: 100px;
                width: 330px;
                overflow: hidden;
                text-overflow: ellipsis;
                animation-fill-mode: forwards;
                animation-duration: 2s;
                animation-iteration-count: infinite;
                animation-name: bg-change;
                @include shared.site() using($site-key){
                    border: 1px solid shared.theme-color('comp2_3', $site-key);
                }
            }
            @keyframes silent-notif-display {
                0%{
                    opacity: 0;
                    transform: scale(0);
                }
                10%{
                    opacity: 1;
                    transform: scale(1.2);
                }
                35%{
                    opacity: 1;
                    transform: scale(1);
                }
                90%{
                    opacity: 1;
                    transform: scale(1);
                }
                100%{
                    opacity: 0;
                    transform: scale(0);
                }
            }
            @keyframes bg-change {
                0%{
                    background-color: var(--theme-color-comp2-5);
                }
                50%{
                    background-color: var(--theme-color-comp2-4);
                }
                100%{
                    background-color: var(--theme-color-comp2-5);
                }
            }
            &[data-mobile]{
                bottom: -45px;
            }
        }
    }
</style>